import { ChangeDetectorRef, Component, DestroyRef, effect, EffectRef, ElementRef, inject, OnInit, Signal, ViewChild } from '@angular/core';
import { DvmService } from '@services/dvm.service';
import { PreviewComponent } from './preview/preview.component';
import { Viewer3dEvent, Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Viewer3dRelatedPreviewObject, Viewer3dRelatedPreviewsEventObject } from '@3ddv/dvm-internal';
import anime from 'animejs';
import { RelatedPreviewsService } from '@services/components/related-previews.service';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';

@Component({
    selector: 'app-related-previews',
    imports: [ PreviewComponent],
    standalone: true,
    templateUrl: './related-previews.component.html',
    styleUrls: ['./related-previews.component.css']
})
export class RelatedPreviewsComponent implements OnInit {

  // VIEWCHILDREN
  @ViewChild('previewsContainer')
  private readonly previewsContainer!:     ElementRef<HTMLDivElement>;
  

  // SERVICES
  private readonly dvmService:             DvmService                                 = inject(DvmService);
  private readonly relatedPreviewsService: RelatedPreviewsService                     = inject(RelatedPreviewsService);
  private readonly destroyRef:             DestroyRef                                 = inject(DestroyRef);
  private readonly cdr:                    ChangeDetectorRef                          = inject(ChangeDetectorRef);


  // GLOBAL STATE                   
  private readonly view:                   Signal<AppViews | null>                    = this.dvmService.view;
  private readonly is3d:                   Signal<boolean>                            = this.dvmService.is3d;
  private readonly viewer3d:               Viewer3dService                            = this.dvmService.viewer3d;
    
    
  // COMPONENT STATE     
  private showElements:                    boolean                                    = false;
  private requestedShowElements:           boolean                                    = false;
  private readonly relatedPreviews:        Map<string, Viewer3dRelatedPreviewObject>  = new Map<string, Viewer3dRelatedPreviewObject>();
  private readonly knownPreviews:          Map<string, Viewer3dRelatedPreviewObject>  = new Map<string, Viewer3dRelatedPreviewObject>();
  protected previews:                      Viewer3dRelatedPreviewObject[]             = [];
  private animationFrame:                  number | null                              = null;
  private animationRequest:                number                                     = 0;
  private changeDetectionRequest:          number                                     = 0;


  // EFFECTS
  private onViewChange:                    EffectRef                                  = effect((): void => {
   
    const currentView: AppViews | null = this.view();
    const is3d: boolean = this.is3d();
   
    if(!currentView) return;

    this.relatedPreviewsService.resetCurrentPreview();

    if(!is3d && this.showElements) this.togglePreviews(false);
    
  });

  // LIFECYCLE METHODS
  ngOnInit(): void {
    
    this.initComponent();

  }

  // METHODS
  private initComponent(): void {
    
    this.viewer3d.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.initSuscriptions());

  }

  private initSuscriptions(): void {

    // UPDATE RELATED PREVIEWS
    this.viewer3d.getObservable('update_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: Viewer3dEvent<Viewer3dRelatedPreviewsEventObject>): void => this.updateRelatedPreviews(event['event']['related_previews']));

    // SHOW RELATED PREVIEWS
    this.viewer3d.getObservable('show_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => this.togglePreviews(true));

    // HIDE RELATED PREVIEWS
    this.viewer3d.getObservable('hide_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => this.togglePreviews(this.is3d()));

    // RESET RELATED PREVIEWS
    this.viewer3d.getObservable('reset_related_preview').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {
      if(!this.is3d()) this.togglePreviews(false);
    });

  }

  private updateRelatedPreviews(relatedPreview: Viewer3dRelatedPreviewObject): void  {

    this.rememberPreview(relatedPreview);

    const currentPreview: Viewer3dRelatedPreviewObject | undefined = this.relatedPreviews.get(relatedPreview.id);

    // El SDK actualiza la ranura pulsada con la vista anterior, pero conserva el
    // label de la relación. Si ya hicimos el intercambio local, mantenemos su label real.
    if(currentPreview?.view_id === relatedPreview.view_id && currentPreview.venue_id === relatedPreview.venue_id) return;

    this.relatedPreviews.set(relatedPreview.id, relatedPreview);
    this.syncPreviews();

    if(this.requestedShowElements || this.showElements) this.scheduleAnimation(true);
    
  }
  
  private togglePreviews(show: boolean): void {

    this.requestedShowElements = show;

    if(this.showElements === show) return;

    if(show && this.previews.length === 0) return;

    this.scheduleAnimation(show);

  }

  private scheduleAnimation(show: boolean): void {

    const animationRequest: number = ++this.animationRequest;

    if(this.animationFrame !== null) cancelAnimationFrame(this.animationFrame);

    this.animationFrame = requestAnimationFrame((): void => {
      this.animationFrame = null;
      if(animationRequest !== this.animationRequest) return;

      this.cdr.markForCheck();
      this.animationFrame = requestAnimationFrame((): void => {
        this.animationFrame = null;
        this.animatePreviews(show);
      });
    });

  }

  private animatePreviews(show: boolean): void {

    const targets = Array.from(this.previewsContainer.nativeElement.children);

    if(targets.length === 0) return;

    anime.remove(targets);

    anime({
        targets: targets,
        opacity: show ? 1 : 0,
        translateY: show ? [20, 0] : [0, 20],
        easing: 'easeInOutQuad',
        delay: anime.stagger(100),
        duration: 300,
        complete: (): void => { 
            if(!show) {
                this.relatedPreviews.clear();
                this.knownPreviews.clear();
                this.syncPreviews();
                this.showElements = false;
                this.requestedShowElements = false;
            } else {
                this.showElements = true;
                this.requestedShowElements = true;
            }

            this.cdr.markForCheck();
        } 
    });

  }

  private syncPreviews(): void {

    this.previews = Array.from(this.relatedPreviews.values());

    const changeDetectionRequest: number = ++this.changeDetectionRequest;

    requestAnimationFrame((): void => {
      if(changeDetectionRequest !== this.changeDetectionRequest) return;
      this.cdr.markForCheck();
    });

  }

  protected checkPreviews(preview: Viewer3dRelatedPreviewObject): void {

    const currentPreview: Viewer3dRelatedPreviewObject | null = this.getCurrentPreview(preview.id);

    this.rememberPreview(preview);
    this.relatedPreviewsService.currentPreview.set(preview);

    if(currentPreview && currentPreview.view_id !== preview.view_id) {
      this.relatedPreviews.set(preview.id, currentPreview);
      this.syncPreviews();
    }

  }

  private getCurrentPreview(id: string): Viewer3dRelatedPreviewObject | null {

    const venueId: string | null = this.viewer3d.getVenueId(),
          viewId: string | null  = this.viewer3d.getViewId(),
          setId: string | null   = this.viewer3d.getSetId();

    if(!venueId || !viewId) return null;

    const key: string = this.getPreviewKey(venueId, viewId, setId),
          knownPreview: Viewer3dRelatedPreviewObject | undefined = this.knownPreviews.get(key);

    if(knownPreview) return { ...knownPreview, id };

    const currentPreview: Viewer3dRelatedPreviewObject = {
      id,
      venue_id: venueId,
      view_id: viewId,
      ...(setId ? { set_id: setId } : {}),
      label: this.getCurrentPreviewLabel(id)
    };

    this.rememberPreview(currentPreview);

    return currentPreview;

  }

  private getCurrentPreviewLabel(id: string): string | null {

    const related = this.viewer3d.related;

    if(!related) return null;

    const relatedViewLabel: string | null | undefined = related.getRelatedViewByName(id)?.label_back;

    if(relatedViewLabel) return relatedViewLabel;

    return Object.values(related.getRelatedViews() ?? {}).find((view): boolean => Boolean(view.label_back))?.label_back ?? null;

  }

  private rememberPreview(preview: Viewer3dRelatedPreviewObject): void {

    const key: string = this.getPreviewKey(preview.venue_id, preview.view_id, preview.set_id ?? null);

    if(!this.knownPreviews.has(key)) this.knownPreviews.set(key, { ...preview });

  }

  private getPreviewKey(venueId: string, viewId: string, setId: string | null): string {

    return `${venueId}:${setId ?? ''}:${viewId}`;

  }

  protected handlePreviewError(preview: Viewer3dRelatedPreviewObject): void {
    
    // Removemos el preview que falló del Map
    this.relatedPreviews.delete(preview.id);
    this.syncPreviews();
    
    // Si no quedan previews, ocultamos el contenedor
    if (this.relatedPreviews.size === 0) {
      this.showElements = false;
      this.requestedShowElements = false;
      this.cdr.markForCheck();
    }
    
  }

}
